import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Button, Grid, Link, Snackbar, Tab, Tabs, Typography,} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import {ExistingCredential, SnackbarState, TokenAction} from './types';
import {ExistingCredentialResponse, getExistingCredentials, revokeApiCredential,} from './api-integration-requests';
import {ConfirmationDialog, CreateTokenPanel, EditTokenPanel, EventLogsTable, TokensTable} from './components';
import {formatDateMMDDyyyy} from '../../util/DateUtils';
import {SecurSpaceReportContainerRoot} from "../../components/common/reports/SecurSpaceReportContainer";

/**
 * API Integration page for GMS_PRO suppliers.
 * Allows creating and managing API tokens for third-party integrations.
 */
const ApiIntegration: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Data state
  const [existingCredentials, setExistingCredentials] = useState<ExistingCredential[]>([]);
  const [loadingCredentials, setLoadingCredentials] = useState(true);

  // Drawer state
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState<ExistingCredential | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // UI feedback state
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string | React.ReactNode;
    confirmLabel: string;
    confirmColor: 'primary' | 'error' | 'warning';
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    confirmLabel: '',
    confirmColor: 'error',
    onConfirm: () => {},
  });

  /**
   * Maps backend response (snake_case) to UI model (camelCase)
   */
  const mapResponseToCredential = (response: ExistingCredentialResponse): ExistingCredential => ({
    id: response.id,
    key: response.key_prefix,
    description: response.description || '',
    createdDate: response.creation_date ? formatDateMMDDyyyy(response.creation_date) : '',
    lastUsedDate: response.last_used_at ? formatDateMMDDyyyy(response.last_used_at) : undefined,
    active: response.active,
    email: response.email || undefined,
    firstName: response.first_name || undefined,
    lastName: response.last_name || undefined,
    phoneNumber: response.phone_number || undefined,
    companyName: response.company_name || undefined,
  });

  // Fetch existing credentials
  const fetchCredentials = useCallback(async () => {
    setLoadingCredentials(true);
    try {
      const response = await getExistingCredentials();
      const credentials = (response.body || []).map(mapResponseToCredential);
      setExistingCredentials(credentials);
    } catch (err) {
      console.error('Failed to fetch credentials:', err);
      showSnackbar('Failed to load API credentials', 'error');
    } finally {
      setLoadingCredentials(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  // Snackbar helper
  const showSnackbar = (message: string, severity: SnackbarState['severity'] = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close confirmation dialog
  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  // Handlers
  const handleOpenDrawer = () => {
    setCreateDrawerOpen(true);
  };

  const handleCloseCreateDrawer = () => {
    setCreateDrawerOpen(false);
  };

  const handleCloseEditDrawer = () => {
    setEditDrawerOpen(false);
    setEditingCredential(null);
  };

  const handleTokenCreated = () => {
    fetchCredentials();
    showSnackbar('API token created successfully');
  };

  const handleTokenUpdated = () => {
    fetchCredentials();
    showSnackbar('API token updated successfully');
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Action handlers
  const handleRevoke = async (credential: ExistingCredential) => {
    try {
      await revokeApiCredential(credential.id);
      fetchCredentials();
      showSnackbar(`Token "${credential.description || credential.key}" has been revoked`);
    } catch (_err) {
      showSnackbar('Failed to revoke token', 'error');
    }
  };

  const handleTokenAction = (action: TokenAction, credential: ExistingCredential) => {
    const tokenName = credential.description || credential.key;

    switch (action) {
      case 'edit':
        setEditingCredential(credential);
        setEditDrawerOpen(true);
        break;

      case 'revoke':
        setConfirmDialog({
          open: true,
          title: 'Revoke API Token?',
          message: (
            <>
              Are you sure you want to revoke <strong>{tokenName}</strong>?
              <br /><br />
              This action <strong>cannot be undone</strong>. Any integrations using this token will stop working immediately.
            </>
          ),
          confirmLabel: 'Revoke',
          confirmColor: 'error',
          onConfirm: () => {
            closeConfirmDialog();
            handleRevoke(credential);
          },
        });
        break;
    }
  };

  return (
    <SecurSpaceReportContainerRoot container>
      <Grid component={'article'} container>
        {/* Header */}
        <Grid item container justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant={'h5'} component={'h1'} color={'textPrimary'}>
            API Integration
          </Typography>
          {activeTab === 0 && (
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={handleOpenDrawer}
            >
              Create API Token
            </Button>
          )}
        </Grid>

        {/* Documentation Banner */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert
            icon={<MenuBookIcon />}
            severity="info"
            sx={{
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between'
              }
            }}
          >
            <span>Need help integrating? Check out our API documentation for authentication examples and endpoint reference.</span>
            <Link
              href="https://docs.secur.space/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ml: 2, whiteSpace: 'nowrap', fontWeight: 500 }}
            >
              View Docs →
            </Link>
          </Alert>
        </Grid>

        {/* Tabs */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_e, newValue) => setActiveTab(newValue)}
            aria-label="API integration sections"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="API Keys" />
            <Tab label="Logs" />
          </Tabs>
        </Grid>

        {/* Tab Content: API Keys */}
        {activeTab === 0 && (
          <TokensTable
            credentials={existingCredentials}
            loading={loadingCredentials}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onAction={handleTokenAction}
          />
        )}

        {/* Tab Content: Logs */}
        {activeTab === 1 && (
          <EventLogsTable credentials={existingCredentials} />
        )}

        {/* Create Token Panel */}
        <CreateTokenPanel
          open={createDrawerOpen}
          onClose={handleCloseCreateDrawer}
          onTokenCreated={handleTokenCreated}
        />

        {/* Edit Token Panel */}
        <EditTokenPanel
          open={editDrawerOpen}
          credential={editingCredential}
          onClose={handleCloseEditDrawer}
          onTokenUpdated={handleTokenUpdated}
        />

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          confirmColor={confirmDialog.confirmColor}
          onConfirm={confirmDialog.onConfirm}
          onCancel={closeConfirmDialog}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Grid>
    </SecurSpaceReportContainerRoot>
  );
};

export default ApiIntegration;
